'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { rewriteLegacyHtml } from '@/lib/legacy-html';

export interface TestimonialItem {
  quoteHtml: string;
  name?: string | undefined;
  company?: string | undefined;
  logo?: string | undefined;
}

const AUTO_SCROLL_PX_PER_FRAME = 0.4;

/**
 * Card height is set so the quote area clears five 14px/1.75 lines (~123px)
 * with the sixth peeking under the fade — the cue that the card has more to
 * read. Changing the card padding, the quote mark row, the "Read full quote"
 * row or the caption row changes that budget, so re-check it here rather than
 * nudging the class.
 */
const CARD_HEIGHT_CLASS = 'h-[19rem]';

/**
 * Client-quote carousel — fixed-height cards whose long quotes scroll inside
 * the card (with a fade + "Read full quote" button opening the whole thing in
 * a dialog), slow continuous auto-scroll that pauses on hover/focus/touch and
 * respects prefers-reduced-motion, plus manual prev/next buttons.
 */
export function TestimonialCarousel({
  heading,
  testimonials,
  rows = 1,
  className = 'mt-16',
}: {
  heading: string;
  testimonials: readonly TestimonialItem[];
  rows?: 1 | 2 | 3;
  /** Outer spacing; pass '' when the host section already pads. */
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  // The dialog covers the carousel, so hover/focus can't be trusted to keep
  // the drift paused while it is open.
  const expandedRef = useRef(false);
  const [expanded, setExpanded] = useState<TestimonialItem | null>(null);

  useEffect(() => {
    expandedRef.current = expanded !== null;
    pausedRef.current = expanded !== null;
  }, [expanded]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const tracks = [...container.querySelectorAll<HTMLDivElement>('[data-track]')];
    let raf = 0;
    const step = () => {
      if (!pausedRef.current) {
        for (const track of tracks) {
          if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 1) {
            track.scrollLeft = 0;
          } else {
            track.scrollLeft += AUTO_SCROLL_PX_PER_FRAME;
          }
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const pause = () => {
      pausedRef.current = true;
    };
    const resume = () => {
      if (!expandedRef.current) pausedRef.current = false;
    };
    container.addEventListener('pointerenter', pause);
    container.addEventListener('pointerleave', resume);
    container.addEventListener('touchstart', pause, { passive: true });
    container.addEventListener('focusin', pause);
    container.addEventListener('focusout', resume);
    return () => {
      cancelAnimationFrame(raf);
      container.removeEventListener('pointerenter', pause);
      container.removeEventListener('pointerleave', resume);
      container.removeEventListener('touchstart', pause);
      container.removeEventListener('focusin', pause);
      container.removeEventListener('focusout', resume);
    };
  }, []);

  const closeExpanded = useCallback(() => setExpanded(null), []);

  if (testimonials.length === 0) return null;

  const perRow = Math.ceil(testimonials.length / rows);
  const rowItems = Array.from({ length: rows }, (_, r) =>
    testimonials.slice(r * perRow, (r + 1) * perRow),
  ).filter((r) => r.length > 0);

  const scrollByCard = (direction: -1 | 1) => {
    const container = containerRef.current;
    if (!container) return;
    // Hold the drift while the manual scroll animates, otherwise the
    // per-frame auto-scroll fights the smooth scroll and nothing moves.
    pausedRef.current = true;
    for (const track of container.querySelectorAll<HTMLDivElement>('[data-track]')) {
      const card = track.querySelector('figure');
      const step = card ? card.getBoundingClientRect().width + 24 : track.clientWidth;
      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }
    window.setTimeout(() => {
      if (!expandedRef.current) pausedRef.current = false;
    }, 700);
  };

  return (
    <section className={className} aria-label={heading}>
      <div className="flex items-center justify-between gap-4">
        <h2 className="heading-lg">{heading}</h2>
        <div className="flex gap-2">
          {(
            [
              [-1, 'Previous testimonials', 'm14 6-6 6 6 6'],
              [1, 'Next testimonials', 'm10 6 6 6-6 6'],
            ] as const
          ).map(([direction, label, path]) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              onClick={() => scrollByCard(direction)}
              className="rounded-full border border-brand-navy/15 p-2.5 text-brand-navy transition-colors hover:border-brand-blue hover:text-brand-blue"
            >
              <svg aria-hidden viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={path} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ))}
        </div>
      </div>
      <div ref={containerRef} className="mt-6 space-y-6">
        {rowItems.map((row, rowIndex) => (
          <div
            key={rowIndex}
            data-track
            className="flex gap-6 overflow-x-auto pb-2 [scrollbar-width:thin]"
          >
            {row.map((testimonial, index) => (
              <TestimonialCard
                key={`${testimonial.name ?? 'quote'}-${index}`}
                testimonial={testimonial}
                onExpand={() => setExpanded(testimonial)}
              />
            ))}
          </div>
        ))}
      </div>
      <TestimonialDialog testimonial={expanded} onClose={closeExpanded} />
    </section>
  );
}

/**
 * One ResizeObserver for every card on the page — /about/reviews renders 742
 * of them, and an observer each is a lot of machinery for "did this quote
 * still overflow after the layout changed width".
 */
const resizeCallbacks = new WeakMap<Element, () => void>();
let sharedResizeObserver: ResizeObserver | undefined;

function observeResize(element: Element, onResize: () => void): () => void {
  sharedResizeObserver ??= new ResizeObserver((entries) => {
    for (const entry of entries) resizeCallbacks.get(entry.target)?.();
  });
  resizeCallbacks.set(element, onResize);
  sharedResizeObserver.observe(element);
  return () => {
    resizeCallbacks.delete(element);
    sharedResizeObserver?.unobserve(element);
  };
}

function TestimonialCard({
  testimonial,
  onExpand,
}: {
  testimonial: TestimonialItem;
  onExpand: () => void;
}) {
  const quoteRef = useRef<HTMLQuoteElement>(null);
  const [clipped, setClipped] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const quote = quoteRef.current;
    if (!quote) return undefined;
    const measure = () => setClipped(quote.scrollHeight - quote.clientHeight > 1);
    measure();
    // Webfont swap re-wraps the quote, which can add or remove the sixth line.
    void document.fonts.ready.then(measure);
    return observeResize(quote, measure);
  }, [testimonial.quoteHtml]);

  const showCue = clipped && !atEnd;
  const attribution = testimonial.name ?? testimonial.company ?? 'this client';

  return (
    <figure
      className={`flex ${CARD_HEIGHT_CLASS} w-[320px] shrink-0 flex-col rounded-2xl border border-brand-navy/10 bg-white p-6 shadow-sm sm:w-[380px]`}
    >
      <div aria-hidden className="h-5 font-serif text-5xl leading-none text-brand-royal/25">
        &ldquo;
      </div>
      <div className="relative mt-1 min-h-0 flex-1">
        <blockquote
          ref={quoteRef}
          onScroll={(event) => {
            const el = event.currentTarget;
            setAtEnd(el.scrollTop >= el.scrollHeight - el.clientHeight - 1);
          }}
          tabIndex={0}
          className="wp-content h-full overflow-y-auto pr-2 text-sm [&>p:first-child]:mt-0 [scrollbar-width:thin] rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-royal"
          dangerouslySetInnerHTML={{ __html: rewriteLegacyHtml(testimonial.quoteHtml) }}
        />
        {showCue ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white via-white/85 to-transparent"
          />
        ) : null}
      </div>
      {clipped ? (
        <div className="mt-2 flex justify-end">
          {/*
            The accessible name is an aria-label rather than an `sr-only` span:
            `sr-only` is position:absolute, and nothing between here and <body>
            is a containing block, so off-screen cards' spans escape the
            track's overflow-x clip and widen the whole document. It keeps the
            visible text as a prefix (WCAG 2.5.3 label-in-name).
          */}
          <button
            type="button"
            onClick={onExpand}
            aria-label={`Read full quote from ${attribution}`}
            className="rounded-full px-2.5 py-1 text-xs font-bold text-brand-royal ring-1 ring-brand-royal/30 transition-colors hover:bg-brand-royal hover:text-white hover:ring-brand-royal"
          >
            Read full quote
          </button>
        </div>
      ) : null}
      {testimonial.name || testimonial.company || testimonial.logo ? (
        <figcaption className="mt-4 flex items-center justify-between gap-3 border-t border-brand-navy/10 pt-4">
          <div className="min-w-0">
            {testimonial.name ? (
              <p className="truncate text-sm font-bold text-brand-navy">{testimonial.name}</p>
            ) : null}
            {testimonial.company ? (
              <p className="truncate text-xs text-brand-navy/70">{testimonial.company}</p>
            ) : null}
          </div>
          {testimonial.logo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={testimonial.logo}
              alt={testimonial.company ?? ''}
              loading="lazy"
              className="h-9 w-auto max-w-[110px] shrink-0 rounded-lg bg-white object-contain"
            />
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The whole quote, unclipped — Esc or backdrop click closes. */
function TestimonialDialog({
  testimonial,
  onClose,
}: {
  testimonial: TestimonialItem | null;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!testimonial) return undefined;
    const restoreTo = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      restoreTo?.focus();
    };
  }, [testimonial, onClose]);

  if (!testimonial) return null;

  const attribution = testimonial.name ?? testimonial.company ?? 'a client';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Full quote from ${attribution}`}
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-navy/80 p-4 sm:p-10"
    >
      <div
        ref={panelRef}
        onClick={(event) => event.stopPropagation()}
        className="relative flex max-h-full w-full max-w-2xl flex-col rounded-2xl bg-white p-6 shadow-xl sm:p-8"
      >
        <button
          ref={closeRef}
          type="button"
          aria-label="Close quote"
          onClick={onClose}
          className="absolute top-4 right-4 rounded-full p-2 text-brand-navy transition-colors hover:bg-brand-mist"
        >
          <svg aria-hidden viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
          </svg>
        </button>
        <div aria-hidden className="h-6 font-serif text-6xl leading-none text-brand-royal/25">
          &ldquo;
        </div>
        <blockquote
          tabIndex={0}
          className="wp-content mt-2 overflow-y-auto pr-2 [&>p:first-child]:mt-0 [scrollbar-width:thin] rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-royal"
          dangerouslySetInnerHTML={{ __html: rewriteLegacyHtml(testimonial.quoteHtml) }}
        />
        {testimonial.name || testimonial.company || testimonial.logo ? (
          <div className="mt-6 flex items-center justify-between gap-4 border-t border-brand-navy/10 pt-4">
            <div className="min-w-0">
              {testimonial.name ? (
                <p className="font-bold text-brand-navy">{testimonial.name}</p>
              ) : null}
              {testimonial.company ? (
                <p className="text-sm text-brand-navy/70">{testimonial.company}</p>
              ) : null}
            </div>
            {testimonial.logo ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={testimonial.logo}
                alt={testimonial.company ?? ''}
                className="h-11 w-auto max-w-[140px] shrink-0 rounded-lg bg-white object-contain"
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
